import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export default function AnalyzingTextScanner({
  initialText = `Analyze this text.\nEach line will be scanned from left to right by a green vertical cursor.\nThe active line is subtly highlighted.\nEdit this content — the scanner reflows automatically.`,
  speed = 100,
  cursorColor = "#00ff6a",
}: {
  initialText?: string;
  speed?: number;
  cursorColor?: string;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLDivElement | null>(null);
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const lineHiRef = useRef<HTMLDivElement | null>(null);
  const linesRef = useRef<Array<{ left: number; right: number; top: number; height: number }>>([]);
  const lineIdxRef = useRef(0);
  const xRef = useRef(0);
  const lastTsRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const dirRef = useRef(1);
  const statusesRef = useRef<string[]>(["Improving Speech...", "Remove Background Noise...", "Enhancing Clarity..."]);
  const [statusIndex, setStatusIndex] = useState(0);

  const getDisplayText = useCallback((text: string) => {
    const trimmedText = text.trim();
    const maxChars = 200;
    if (trimmedText.length <= maxChars) {
      return trimmedText;
    }
    const truncated = trimmedText.slice(-maxChars).trim();
    const firstSpaceIndex = truncated.indexOf(' ');
    const cleanTruncated = firstSpaceIndex > 0 ? truncated.slice(firstSpaceIndex + 1) : truncated;
    return `...${cleanTruncated}`;
  }, []);

  const displayText = getDisplayText(initialText);

  const getLines = useCallback(() => {
    const textEl = textRef.current;
    const boxEl = boxRef.current;
    if (!textEl || !boxEl) return [] as typeof linesRef.current;
    const range = document.createRange();
    range.selectNodeContents(textEl);
    const rects = Array.from(range.getClientRects());
    // range.detach?.(); // detach might not exist in all environments or strict mode ts

    const boxRect = boxEl.getBoundingClientRect();
    const raw = rects
      .map((r) => ({
        left: r.left - boxRect.left,
        right: r.right - boxRect.left,
        top: r.top - boxRect.top,
        width: r.width,
        height: r.height,
      }))
      .filter((r) => r.width > 0.5 && r.height > 0.5)
      .sort((a, b) => (a.top - b.top) || (a.left - b.left));

    const grouped: Array<{ left: number; right: number; top: number; height: number }> = [];
    const tol = 2;
    for (const r of raw) {
      const g = grouped.find((g) => Math.abs(g.top - r.top) < tol);
      if (g) {
        g.left = Math.min(g.left, r.left);
        g.right = Math.max(g.right, r.right);
      } else {
        grouped.push({ left: r.left, right: r.right, top: r.top, height: r.height });
      }
    }
    return grouped.map((g) => ({ left: g.left, right: g.right, top: g.top, height: g.height }));
  }, []);

  const positionOnLine = useCallback((i: number, px: number) => {
    const L = linesRef.current[i];
    const cursor = cursorRef.current;
    const hi = lineHiRef.current;
    if (!L || !cursor || !hi) return;

    cursor.style.transform = `translate(${px}px, ${L.top}px)`;
    cursor.style.height = `${L.height}px`;

    hi.style.transform = `translate(0px, ${L.top}px)`;
    hi.style.height = `${L.height}px`;
  }, []);

  const layout = useCallback(() => {
    linesRef.current = getLines();
    lineIdxRef.current = 0;
    xRef.current = linesRef.current[0]?.left ?? 0;
    lastTsRef.current = 0;
    dirRef.current = 1;
    if (linesRef.current.length) positionOnLine(0, xRef.current);
  }, [getLines, positionOnLine]);

  const step = useCallback((ts: number) => {
    if (!lastTsRef.current) lastTsRef.current = ts;
    const dt = (ts - lastTsRef.current) / 1000;
    lastTsRef.current = ts;

    const lines = linesRef.current;
    if (lines.length) {
      let i = lineIdxRef.current;
      let L = lines[i];

      if (!L) {
        rafRef.current = requestAnimationFrame(step);
        return;
      }

      xRef.current += dirRef.current * speed * dt;

      if (dirRef.current > 0 && xRef.current >= L.right) {
        xRef.current = L.right;
        dirRef.current = -1;
      } else if (dirRef.current < 0 && xRef.current <= L.left) {
        xRef.current = L.left;
        i = (i + 1) % lines.length;
        lineIdxRef.current = i;
        L = lines[i];
        if (!L) {
          rafRef.current = requestAnimationFrame(step);
          return;
        }
        xRef.current = L.left;
        dirRef.current = 1;
      }

      positionOnLine(lineIdxRef.current, xRef.current);
    }
    rafRef.current = requestAnimationFrame(step);
  }, [positionOnLine, speed]);

  useLayoutEffect(() => {
    layout();
    const onResize = () => layout();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [layout]);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const onInput = () => layout();
    el.addEventListener("input", onInput);
    rafRef.current = requestAnimationFrame(step);
    return () => {
      el.removeEventListener("input", onInput);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [layout, step]);

  useEffect(() => {
    const id = setInterval(() => {
      const len = statusesRef.current.length;
      setStatusIndex((i) => (i + 1) % len);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-full w-full grid place-items-center bg-background rounded-xl">
      <div ref={boxRef} className="relative w-full max-w-3xl bg-white rounded-2xl p-4 sm:p-6">
        <div
          ref={textRef}
          contentEditable
          suppressContentEditableWarning
          className="relative leading-relaxed outline-none text-center md:text-[9px] lg:text-[11px] xl:text-[15px] line-clamp-3"
          style={{ caretColor: "auto", minHeight: "3.6em", maxHeight: "3.6em", overflow: "hidden" }}
          spellCheck={false}
        >
          {displayText}
        </div>
        <div className="mt-4 text-center italic text-emerald-600 md:text-[9px] lg:text-[11px] xl:text-[14px]">
          {statusesRef.current[statusIndex]}
        </div>

        <div className="pointer-events-none absolute inset-0">
          <div
            ref={lineHiRef}
            className="absolute left-0 right-0 rounded"
            style={{ height: "1em", transform: "translateY(0)" }}
          />
          <div
            ref={cursorRef}
            className="absolute rounded"
            style={{
              width: 3,
              height: "1em",
              background: cursorColor,
              boxShadow: `0 0 8px ${cursorColor}, 0 0 16px rgba(0,255,128,0.35)`,
              transform: "translate(0,0)",
            }}
          />
        </div>
      </div>
    </div>
  );
}
